import React from 'react';
import {
  FaBolt,
  FaHeadphones,
  FaChartLine,
  FaTwitch,
  FaSpotify,
  FaRocket,
} from 'react-icons/fa';
import '../../styles/Home.css';

/**
 * Section « Pourquoi choisir StreamingBot ? » avec H2/H3 SEO
 * Structure sémantique selon l'audit SEO
 *
 * @component
 * @returns {JSX.Element} La section des fonctionnalités
 */
const HomeFeatures = () => {
  const features = [
    {
      icon: FaBolt,
      title: 'Commandes chat et récompenses Twitch',
      description:
        'Commandes personnalisées, récompenses de chaîne et points Twitch. Vos viewers interagissent avec votre stream en temps réel.',
      color: 'twitch',
    },
    {
      icon: FaHeadphones,
      title: 'Contrôle musique Spotify en direct',
      description:
        'Vos viewers peuvent demander, voter et contrôler la musique de votre stream. Intégration Spotify native.',
      color: 'spotify',
    },
    {
      icon: FaChartLine,
      title: 'Overlays et statistiques temps réel',
      description:
        'Overlays OBS, dashboard complet et statistiques en direct. Gérez tout depuis une interface unique.',
      color: 'cyan',
    },
  ];

  return (
    <section className="features-section" aria-labelledby="pourquoi-streambot">
      <div className="features-header">
        <h2 id="pourquoi-streambot" className="features-title">
          Pourquoi choisir <span className="features-title-accent">StreamingBot</span> ?
        </h2>
        <p className="features-subtitle">
          StreamingBot est un bot Twitch freemium qui se connecte à Spotify pour vous aider à gérer votre stream.
        </p>
      </div>

      <div className="features-grid features-grid-single-row">
        {features.map((feature, index) => (
          <article
            className={`feature-card feature-card-${feature.color}`}
            key={feature.title}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className={`feature-icon-wrapper ${feature.color}`}>
              <feature.icon className="feature-icon" />
            </div>
            <div className="feature-card-text">
              <h3 className="feature-card-title">{feature.title}</h3>
              <p className="feature-card-description">{feature.description}</p>
            </div>
            <div className="feature-card-glow"></div>
          </article>
        ))}
      </div>
    </section>
  );
};

/**
 * Section « Comment ça marche ? » avec H2/H3 SEO
 *
 * @component
 * @returns {JSX.Element} La section étapes
 */
export const HomeHowItWorks = () => {
  const steps = [
    {
      icon: FaTwitch,
      title: 'Connectez Twitch',
      description: 'Authentifiez-vous avec votre compte Twitch en 1 clic via OAuth.',
      color: 'twitch',
    },
    {
      icon: FaSpotify,
      title: 'Liez Spotify',
      description: 'Associez votre compte Spotify pour le contrôle musical de votre stream.',
      color: 'spotify',
    },
    {
      icon: FaRocket,
      title: 'Accédez au dashboard',
      description: 'Configurez commandes, récompenses et overlays depuis votre espace.',
      color: 'cyan',
    },
  ];

  return (
    <section className="features-section how-it-works-section" aria-labelledby="comment-ca-marche">
      <div className="features-header">
        <h2 id="comment-ca-marche" className="features-title">
          Comment ça marche ?
        </h2>
        <p className="features-subtitle">
          En trois étapes simples, votre bot est opérationnel.
        </p>
      </div>

      <div className="how-it-works-steps">
        {steps.map((step, index) => (
          <article
            className={`feature-card feature-card-${step.color} how-it-works-step`}
            key={step.title}
            style={{ animationDelay: `${index * 0.15}s` }}
          >
            <span className="step-number" aria-hidden="true">
              {index + 1}
            </span>
            <div className={`feature-icon-wrapper ${step.color}`}>
              <step.icon className="feature-icon" />
            </div>
            <div className="feature-card-text">
              <h3 className="feature-card-title">{step.title}</h3>
              <p className="feature-card-description">{step.description}</p>
            </div>
            <div className="feature-card-glow"></div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default HomeFeatures;
